/**
 * TermsPageUserClient - Authenticated user terms/waivers page component
 *
 * Displays waivers for logged-in users with Dashboard styling
 *
 * @example
 * ```tsx
 * <TermsPageUserClient />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/contexts";
import { excludeSurchargeDisclaimerPolicies } from "@/features/policies/shared/policy-member-visibility";
import { usePoliciesByUserIdGet } from "@/hooks";
import { Heading, Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { dashboardScrollMarginBelowTopBar } from "@/lib/layout/dashboard-sticky-top";
import {
  TermsPageEmpty,
  TermsPageError,
  TermsPageSkeleton,
  WaiversList,
  waiverAnchorId,
} from "./components";
import {
  buildTermsMemberNavItems,
  TermsMemberNav,
} from "./member/navigation";

const CONTENT_MAX_W = "1044px";

export const TermsPageUserClient = () => {
  const theme = useThemeTokens();
  const user = useAuth();
  const userId = user?.id;
  const tenantId = resolveEffectiveTenantNumber();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: true },
  );

  const {
    data: policiesData,
    isLoading: isLoadingWaivers,
    isPending: isPendingWaivers,
    isSuccess: isWaiversSuccess,
    isError,
    isFetching,
    refetch,
  } = usePoliciesByUserIdGet({
    userId: userId ?? 0,
    page: 1,
    pageSize: 1000,
  });

  const waiversList = useMemo(
    () => excludeSurchargeDisclaimerPolicies(policiesData?.items),
    [policiesData?.items],
  );
  const navItems = useMemo(
    () => buildTermsMemberNavItems(waiversList),
    [waiversList],
  );
  const [activeWaiverId, setActiveWaiverId] = useState<string>("");

  const showInitialLoading =
    !userId || ((isLoadingWaivers || isPendingWaivers) && !isWaiversSuccess);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (waiversList.length === 0) {
      setActiveWaiverId("");
      return;
    }

    const hash = window.location.hash.replace(/^#/, "");
    const waiverFromHash = waiversList.find(
      (waiver) => waiverAnchorId(waiver.policyId) === hash,
    );

    setActiveWaiverId(
      waiverFromHash
        ? String(waiverFromHash.policyId)
        : String(waiversList[0]?.policyId ?? ""),
    );
  }, [waiversList]);

  const activeWaiver = useMemo(() => {
    if (!activeWaiverId) return waiversList[0] ?? null;
    return (
      waiversList.find((waiver) => String(waiver.policyId) === activeWaiverId) ??
      waiversList[0] ??
      null
    );
  }, [activeWaiverId, waiversList]);

  const mobileWaivers = useMemo(
    () => (activeWaiver ? [activeWaiver] : []),
    [activeWaiver],
  );

  useEffect(() => {
    if (waiversList.length < 2) return;

    const mediaQuery = window.matchMedia("(min-width: 992px)");

    let observer: IntersectionObserver | null = null;

    const setupObserver = () => {
      observer?.disconnect();
      observer = null;

      if (!mediaQuery.matches) return;

      observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          const topEntry = visibleEntries[0];
          if (!topEntry) return;

          const matchedWaiver = waiversList.find(
            (waiver) => waiverAnchorId(waiver.policyId) === topEntry.target.id,
          );

          if (matchedWaiver) {
            setActiveWaiverId(String(matchedWaiver.policyId));
          }
        },
        {
          rootMargin: "-15% 0px -55% 0px",
          threshold: [0, 0.2, 0.5, 1],
        },
      );

      waiversList.forEach((waiver) => {
        const element = document.getElementById(waiverAnchorId(waiver.policyId));
        if (element) observer?.observe(element);
      });
    };

    setupObserver();
    mediaQuery.addEventListener("change", setupObserver);

    return () => {
      mediaQuery.removeEventListener("change", setupObserver);
      observer?.disconnect();
    };
  }, [waiversList]);

  const handleJumpTo = useCallback((waiverId: string) => {
    setActiveWaiverId(waiverId);
  }, []);

  return (
    <Flex direction="column" px={{ base: 4, lg: 8 }} pb={8} pt={{ base: 6, lg: 8 }}>
      <Box w="100%" maxW={CONTENT_MAX_W} mb={6}>
        <Heading
          fontSize={{ base: "2xl", md: "3xl" }}
          letterSpacing="-0.02em"
          mb={2}
          fontWeight="600"
          color={theme.text.primary}
        >
          {contentSettings.termsTitle}
        </Heading>
        <Text variant="secondary" fontSize={{ base: "sm", md: "md" }} maxW="2xl" lineHeight="1.6">
          {contentSettings.termsDescription}
        </Text>
      </Box>

      <Box w="100%" maxW={CONTENT_MAX_W}>
        {showInitialLoading ? (
          <TermsPageSkeleton
            showHeading={false}
            showStatus
            showSidebarLayout
          />
        ) : isError ? (
          <TermsPageError onRetry={handleRetry} isRetrying={isFetching} />
        ) : waiversList.length === 0 ? (
          <TermsPageEmpty variant="portal" />
        ) : (
          <Flex
            align="flex-start"
            gap={6}
            direction={{ base: "column", lg: "row" }}
            w="100%"
            minW={0}
          >
            <TermsMemberNav
              items={navItems}
              activeKey={activeWaiverId}
              onJumpTo={handleJumpTo}
            />

            <Box flex={1} minW={0} w="100%">
              <Box display={{ base: "block", lg: "none" }}>
                <WaiversList
                  key={activeWaiverId}
                  waivers={mobileWaivers}
                  allowSign
                  userEmail={user?.email ?? undefined}
                  scrollMarginTop={dashboardScrollMarginBelowTopBar()}
                />
              </Box>
              <Box display={{ base: "none", lg: "block" }}>
                <WaiversList
                  waivers={waiversList}
                  allowSign
                  userEmail={user?.email ?? undefined}
                  scrollMarginTop={dashboardScrollMarginBelowTopBar()}
                />
              </Box>
            </Box>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
